// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Complaints  (route /complaint[?order=REF] — the "Raise a Complaint" row on the profile page)
// A four-step form for a DELIVERED order: 1 pick the affected item(s) (or the whole order), 2 what went wrong, 3 describe it,
// then send. It creates a claim (utils/claims → POST /api/me/orders/{ref}/claim): one per order, within 24 h of delivery, reviewed
// by a person; if approved the amount is credited to the wallet. With several eligible orders the customer picks one (default: the
// newest, or ?order=). Deliberately NOT here (no data/system behind it): photo/video upload (no file storage — the page points to
// WhatsApp), cold-chain "temperature issue" reason, "AI seafood auditor", instant refunds, a 5 % bonus, free replacement, resolution
// or review-time promises, a named fishmonger, an SMS ticket number.
// Neighbours: pages/RefundAssistance (order list + claim status), pages/Wallet, utils/claims, data/whatsappContext.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CircleAlert, CircleCheck, Loader2, PackageOpen, Receipt, ShieldCheck } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { seafoodItems } from "../data/seafood";
import { getWhatsAppLink } from "../data/whatsappContext";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { CLAIM_REASONS, MAX_NOTE, fetchClaims, submitClaim, type ClaimReason } from "../utils/claims";
import { fetchOrders, formatWhen, reachedAt, type Order } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";
import { getWikimediaSrcSet } from "../utils/wikimediaImage";

type Load = { status: "loading" } | { status: "error" } | { status: "ready"; eligible: Order[]; windowHours: number; blocked: "none" | "reported" | "window" };

/** Delivered, not yet reported, and still inside the window. */
function isEligible(order: Order, reported: Set<string>, windowHours: number, now: number): boolean {
  const delivered = reachedAt(order, "delivered");
  return order.status === "delivered" && !!delivered && !reported.has(order.ref) && new Date(delivered).getTime() + windowHours * 3_600_000 > now;
}

function Step({ n, title, aside }: { n: number; title: string; aside?: string }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="flex items-center gap-2 font-display text-base font-bold text-ocean-950 dark:text-foam-50">
        <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-full bg-ocean-800 text-[11px] font-bold text-white">{n}</span>
        {title}
      </h2>
      {aside && <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">{aside}</span>}
    </div>
  );
}

export default function Complaint() {
  useDocumentTitle("Raise a complaint");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [params] = useSearchParams();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [orderRef, setOrderRef] = useState<string | null>(null);
  const [whole, setWhole] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [reason, setReason] = useState<ClaimReason | null>(null);
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const doneHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    Promise.all([fetchOrders(), fetchClaims()]).then(
      ([orders, claims]) => {
        if (cancelled) return;
        const reported = new Set(claims.claims.map((c) => c.orderRef));
        const now = Date.now();
        const eligible = orders.filter((o) => isEligible(o, reported, claims.windowHours, now));
        const delivered = orders.filter((o) => o.status === "delivered");
        const blocked = eligible.length > 0 ? "none" : delivered.some((o) => reported.has(o.ref)) ? "reported" : "window";
        setLoad({ status: "ready", eligible, windowHours: claims.windowHours, blocked });
        const wanted = params.get("order");
        setOrderRef(eligible.find((o) => o.ref === wanted)?.ref ?? eligible[0]?.ref ?? null);
      },
      () => !cancelled && setLoad({ status: "error" }),
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn, params]);

  useEffect(() => {
    if (sent) doneHeading.current?.focus();
  }, [sent]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const order = load.status === "ready" ? load.eligible.find((o) => o.ref === orderRef) : undefined;
  const nothingPicked = !whole && picked.length === 0;
  const valid = !!order && !nothingPicked && reason !== null;

  const chooseOrder = (ref: string) => {
    setOrderRef(ref);
    setWhole(false);
    setPicked([]);
  };
  const toggleItem = (id: string) => {
    setWhole(false);
    setPicked((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };
  const toggleWhole = () => {
    setWhole((current) => !current);
    setPicked([]);
  };

  const send = async () => {
    setTouched(true);
    if (!order || !valid || reason === null) return;
    setSending(true);
    setError(null);
    try {
      await submitClaim(order.ref, reason, note, whole ? [] : picked);
      setSent(order.ref);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't send that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const shell = (body: ReactNode) => (
    <PageTransition>
      <div className="bg-foam-50 pb-40 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-5 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Raise a complaint</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Tell us what went wrong with a delivery</p>
            </div>
          </header>
          {body}
        </div>
      </div>
    </PageTransition>
  );

  if (sent) {
    return shell(
      <div className="flex flex-col items-center rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
        <span aria-hidden="true" className="flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-teal-700"><CircleCheck className="h-8 w-8" /></span>
        <h2 ref={doneHeading} tabIndex={-1} className="mt-3 font-display text-xl font-bold text-ocean-950 outline-none dark:text-foam-50">Complaint received</h2>
        <p role="status" className="mt-2 text-sm text-ocean-800/90 dark:text-foam-100/85">
          Your complaint about order <strong>#{sent}</strong> is with our team. A person will review it, and the decision — including any wallet credit — will show under &ldquo;Your reports&rdquo; on the refund assistance page.
        </p>
        <Link to="/refund-assistance" className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ocean-800 px-6 font-bold text-white hover:bg-ocean-700">See my reports</Link>
        <Link to="/orders" className="mt-2 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ocean-700/10 px-6 font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Back to orders</Link>
      </div>,
    );
  }

  if (load.status === "loading") {
    return shell(
      <p role="status" className="mt-4 flex items-center justify-center gap-2 text-ocean-900 dark:text-foam-50">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your orders…
      </p>,
    );
  }
  if (load.status === "error") {
    return shell(
      <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
        <p className="font-semibold text-ocean-950 dark:text-foam-50">We couldn&apos;t load your orders.</p>
        <button type="button" onClick={() => window.location.reload()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 font-bold text-white">Try again</button>
      </div>,
    );
  }

  if (!order) {
    return shell(
      <div className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
        <PackageOpen className="mx-auto h-10 w-10 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
        <p className="mt-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
          {load.blocked === "reported" ? "You've already reported your recent delivery" : "No delivery to complain about right now"}
        </p>
        <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">
          A complaint can be raised for a delivered order within {load.windowHours} hours of delivery, once per order.
          {load.blocked === "reported" ? " Its status is on the refund assistance page." : " Nothing you've received is inside that window."}
        </p>
        <Link to="/refund-assistance" className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-ocean-800 px-6 font-bold text-white hover:bg-ocean-700">Refund assistance</Link>
        <Link to={`/contact?subject=${encodeURIComponent("Complaint about an order")}`} className="mt-2 block text-sm font-bold text-ocean-800 underline dark:text-foam-100">Or write to us</Link>
      </div>,
    );
  }

  const delivered = reachedAt(order, "delivered");
  const left = delivered ? Math.max(1, Math.ceil((new Date(delivered).getTime() + load.windowHours * 3_600_000 - Date.now()) / 3_600_000)) : 0;
  const isLatest = load.eligible[0]?.ref === order.ref;

  return shell(
    <form
      className="flex flex-col gap-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {load.eligible.length > 1 ? (
          <div className="flex-1">
            <label htmlFor="complaint-order" className="text-xs font-bold text-ocean-900 dark:text-foam-50">Order</label>
            <select id="complaint-order" value={order.ref} onChange={(event) => chooseOrder(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl bg-white px-3 text-sm text-ocean-950 ring-1 ring-ocean-900/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:text-foam-50">
              {load.eligible.map((o) => (
                <option key={o.ref} value={o.ref}>#{o.ref} · {formatInr(o.total)}</option>
              ))}
            </select>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ocean-700/10 px-3 py-1 text-xs font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">
            <Receipt className="h-4 w-4" aria-hidden="true" /> #{order.ref}
          </span>
        )}
        {isLatest && <span className="rounded-full bg-teal-100 px-2.5 py-1 text-[11px] font-bold text-teal-700">Latest delivered order</span>}
      </div>

      <div className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-ocean-900">
        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <h2 className="font-display text-base font-bold text-ocean-950 dark:text-foam-50">We&apos;ll look into it</h2>
          <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">
            A person at ZenFresh reviews every complaint. If it&apos;s approved, the amount is credited to your wallet. You can raise it for {left} more hour{left === 1 ? "" : "s"} (delivered {delivered ? formatWhen(delivered) : ""}).
          </p>
        </div>
      </div>

          <Link to="/coming-soon/complaint-evidence" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Photo evidence &amp; resolution choice</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
      <section aria-labelledby="step1" className="flex flex-col gap-2">
        <Step n={1} title="Select affected item(s)" aside="More than one is fine" />
        <h3 id="step1" className="sr-only">Affected items</h3>
        <ul className="flex flex-col gap-2">
          {order.items.map((line) => {
            const item = seafoodItems.find((candidate) => candidate.id === line.id);
            const on = picked.includes(line.id);
            return (
              <li key={`${line.id}-${line.pack}`}>
                <label className={`flex min-h-16 cursor-pointer items-center gap-3 rounded-xl p-2.5 shadow-sm ring-1 ring-ocean-900/5 focus-within:outline-2 focus-within:outline-teal-600 dark:ring-white/10 ${on ? "bg-ocean-700/10 dark:bg-white/10" : "bg-white dark:bg-ocean-900"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleItem(line.id)} className="peer sr-only" />
                  {item ? (
                    <img src={item.image} srcSet={getWikimediaSrcSet(item.image)} sizes="56px" alt="" width={56} height={56} loading="lazy" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <span aria-hidden="true" className="h-14 w-14 shrink-0 rounded-lg bg-ocean-700/10" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-ocean-950 dark:text-foam-50">{line.name}</span>
                    <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{line.qty} × {line.pack === "1kg" ? "1 kg" : "500 g"} · {line.cut}</span>
                    <span className="block text-xs font-bold text-ocean-900 dark:text-foam-50">{formatInr(line.lineTotal)}</span>
                  </span>
                  <span aria-hidden="true" className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${on ? "bg-teal-700 text-white" : "bg-ocean-700/10"}`}>{on && <Check className="h-4 w-4" />}</span>
                </label>
              </li>
            );
          })}
          <li>
            <label className={`flex min-h-16 cursor-pointer items-center gap-3 rounded-xl p-2.5 shadow-sm ring-1 ring-ocean-900/5 focus-within:outline-2 focus-within:outline-teal-600 dark:ring-white/10 ${whole ? "bg-ocean-700/10 dark:bg-white/10" : "bg-white dark:bg-ocean-900"}`}>
              <input type="checkbox" checked={whole} onChange={toggleWhole} className="sr-only" />
              <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"><PackageOpen className="h-7 w-7" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">Entire order &amp; delivery</span>
                <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">Packaging, missing items or the delivery itself</span>
              </span>
              <span aria-hidden="true" className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${whole ? "bg-teal-700 text-white" : "bg-ocean-700/10"}`}>{whole && <Check className="h-4 w-4" />}</span>
            </label>
          </li>
        </ul>
        {touched && nothingPicked && <p role="alert" className="flex items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300"><CircleAlert className="h-4 w-4" aria-hidden="true" /> Choose at least one item, or the entire order.</p>}
      </section>

      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">What went wrong?</legend>
        <Step n={2} title="What went wrong?" />
        <div role="radiogroup" aria-label="What went wrong?" className="flex flex-col gap-2">
          {CLAIM_REASONS.map((option) => {
            const on = reason === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setReason(option.id)}
                className={`flex min-h-14 items-center gap-3 rounded-xl p-3 text-left shadow-sm ring-1 ring-ocean-900/5 focus-visible:outline-2 focus-visible:outline-teal-600 dark:ring-white/10 ${on ? "bg-ocean-800 text-white" : "bg-white text-ocean-950 dark:bg-ocean-900 dark:text-foam-50"}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold">{option.label}</span>
                  <span className={`block text-xs ${on ? "text-foam-100" : "text-ocean-800/80 dark:text-foam-100/75"}`}>{option.hint}</span>
                </span>
                {on && <CircleCheck className="h-5 w-5 shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        {touched && reason === null && <p role="alert" className="flex items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300"><CircleAlert className="h-4 w-4" aria-hidden="true" /> Tell us what went wrong.</p>}
      </fieldset>

      <section className="flex flex-col gap-2">
        <Step n={3} title="Describe the issue" aside="Optional" />
        <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-ocean-900">
          <label htmlFor="complaint-note" className="sr-only">Describe the issue</label>
          <textarea
            id="complaint-note"
            value={note}
            maxLength={MAX_NOTE}
            onChange={(event) => setNote(event.target.value)}
            rows={4}
            placeholder="What was wrong with the cut, packaging, freshness or delivery?"
            className="w-full resize-none bg-transparent text-sm text-ocean-950 outline-none placeholder:text-ocean-800/50 dark:text-foam-50"
          />
          <p className="flex justify-between pt-1 text-[11px] text-ocean-800/75 dark:text-foam-100/70">
            <span>The more specific, the easier it is to help.</span>
            <span>{note.length} / {MAX_NOTE}</span>
          </p>
        </div>
        <p className="text-xs text-ocean-800/85 dark:text-foam-100/80">
          Have photos? We can&apos;t take uploads here yet — send them to us on{" "}
          <a href={getWhatsAppLink(`Photos for my complaint about order ${order.ref}`)} target="_blank" rel="noopener noreferrer" className="font-bold underline">WhatsApp<span className="sr-only"> (opens in a new tab)</span></a>{" "}
          with your order number.
        </p>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 px-4 pb-4 pt-3 shadow-[0_-6px_20px_rgba(0,0,0,0.08)] backdrop-blur-md dark:bg-ocean-950/95">
        <div className="mx-auto max-w-xl">
          <p role="alert" className="flex min-h-5 items-center gap-1.5 pb-1 text-sm text-coral-600 dark:text-coral-300">
            {error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {error}
          </p>
          <button type="submit" disabled={sending} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 px-6 text-base font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-60">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : null}
            Submit complaint <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </button>
          <p className="pt-1 text-center text-[11px] text-ocean-800/80 dark:text-foam-100/75">We review every complaint personally; nothing is refunded automatically.</p>
        </div>
      </div>
    </form>,
  );
}
